import { FC } from "react";
import styled from "styled-components";
import Step1 from "../../assets/step1.jpg";
import Step2 from "../../assets/step2.jpg";
import Step4 from "../../assets/step4.png";
import { openInNewTab } from "../../utils/openInNewTab";
import { scrollToAnchor } from "../../utils/scroll";
import { HASH_ROUTES } from "../../constants/routes";
import { useTranslation } from "../../lang/TranslationContext";

type HowItWorksBlockProps = {
  step: number;
};

export const HowItWorksBlock: FC<HowItWorksBlockProps> = ({ step }) => {
  const { translations } = useTranslation();

  const STEPS = [
    {
      img: Step1,
      title: translations.landing.howItWorks.title1,
      description: translations.landing.howItWorks.description1,
      button: translations.landing.howItWorks.button1,
      stepText: translations.landing.howItWorks.stepText1,
    },
    {
      img: Step2,
      title: translations.landing.howItWorks.title2,
      description: translations.landing.howItWorks.description2,
      button: translations.landing.howItWorks.button2,
      stepText: translations.landing.howItWorks.stepText2,
    },
    {
      img: Step4,
      title: translations.landing.howItWorks.title3,
      description: translations.landing.howItWorks.description3,
      button: translations.landing.howItWorks.button3,
      stepText: translations.landing.howItWorks.stepText3,
    },
  ];

  return (
    <Wrap>
      <ContentGrid>
        <ImageSection noImgPadding={step === 2}>
          <img
            src={step === 0 ? Step1 : step === 1 ? Step2 : Step4}
            alt="step"
          />
        </ImageSection>

        <TextSection>
          <StepBadge>
            <span className="number">{step + 1}</span>
            <span className="divider" />
            <span className="text">{STEPS[step].stepText}</span>
          </StepBadge>

          <h2>{STEPS[step].title}</h2>
          <p>{STEPS[step].description}</p>

          <LinkButton
            onClick={() => {
              if (step === 0 || step === 2) {
                openInNewTab("https://marketecho.app/signup");
              }
              if (step === 1) {
                scrollToAnchor(HASH_ROUTES.pricing);
              }
            }}
          >
            {STEPS[step].button}
            <i className="pi pi-arrow-right" />
          </LinkButton>
        </TextSection>
      </ContentGrid>
    </Wrap>
  );
};

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 80px;
  align-items: center;
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 80px;

  @media screen and (max-width: 1200px) {
    gap: 60px;
    padding: 0 60px;
  }

  @media screen and (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 48px;
    padding: 0 40px;
  }

  @media screen and (max-width: 640px) {
    padding: 0 24px;
    gap: 36px;
  }
`;

const ImageSection = styled.div<{ noImgPadding: boolean }>`
  width: 100%;
  height: 100%;
  max-height: 600px;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgb(255, 255, 255);
  padding: ${({ noImgPadding }) => (noImgPadding ? "0" : "48px")};
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(8px);
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  @media screen and (max-width: 968px) {
    max-height: 400px;
    padding: ${({ noImgPadding }) => (noImgPadding ? "0" : "32px")};
  }

  @media screen and (max-width: 640px) {
    max-height: 300px;
    padding: ${({ noImgPadding }) => (noImgPadding ? "0" : "24px")};
  }
`;

const TextSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;

  h2 {
    font-size: 48px;
    font-weight: 600;
    color: #ffffff;
    letter-spacing: -0.02em;
    line-height: 1.2;
    margin: 0;
  }

  p {
    font-size: 18px;
    font-weight: 400;
    color: rgba(255, 255, 255, 0.7);
    line-height: 1.7;
    margin: 0;
    max-width: 600px;
  }

  @media screen and (max-width: 1200px) {
    h2 {
      font-size: 40px;
    }

    p {
      font-size: 17px;
    }
  }

  @media screen and (max-width: 968px) {
    gap: 20px;

    h2 {
      font-size: 36px;
    }

    p {
      font-size: 16px;
    }
  }

  @media screen and (max-width: 640px) {
    gap: 16px;

    h2 {
      font-size: 28px;
    }

    p {
      font-size: 15px;
    }
  }
`;

const StepBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 8px;

  .number {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.2);
    color: #ffffff;
    font-size: 20px;
    font-weight: 700;
    backdrop-filter: blur(8px);
  }

  .divider {
    width: 32px;
    height: 2px;
    background: rgba(255, 255, 255, 0.2);
  }

  .text {
    font-size: 14px;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.8);
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }

  @media screen and (max-width: 640px) {
    gap: 12px;

    .number {
      width: 40px;
      height: 40px;
      font-size: 18px;
    }

    .divider {
      width: 24px;
    }

    .text {
      font-size: 12px;
    }
  }
`;

const LinkButton = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  font-weight: 600;
  font-size: 16px;
  color: #ffffff;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.25);
  padding: 16px 32px;
  margin-top: 12px;
  width: fit-content;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(12px);

  i {
    font-size: 14px;
    transition: transform 0.3s ease;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.18);
    border-color: rgba(255, 255, 255, 0.4);
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);

    i {
      transform: translateX(4px);
    }
  }

  &:active {
    transform: translateY(0);
  }

  @media screen and (max-width: 640px) {
    padding: 14px 28px;
    font-size: 15px;
  }
`;

const Wrap = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px 0 120px;

  @media screen and (max-width: 968px) {
    padding: 20px 0 120px;
  }

  @media screen and (max-width: 640px) {
    padding: 20px 0 120px;
  }
`;
